import React, { createContext, useState, useContext, useMemo, useCallback } from 'react';
import {
	getExperimentCohort,
	VARIATION,
	CONTROL,
	fireStatsigFeatureExposedEvent,
} from './FeatureFlags';
import { useAnalytics } from '../../analytics';
import { Analytics } from './Analytics';
import { useInvitePeopleContext } from '../../components/InvitePeople';
import { defaultPermsApiClient } from '../../clients';
import { useExperimentalFeatures } from '../experimentalFeatures';
import { useProducts } from '../../components/ProductSelect';
import { type InvitedUser, type InvitedUserResult, isUserWithEmail } from '../../types';
import { useInviteeListStateContext } from '../../components/InviteeList';
import { type OptionType } from '../../components/InviteeList/InviteeListContext';
import { filterUsersAndProductsForDisplay } from './filterUsersAndProductsForDisplay';
import { useIntl } from 'react-intl-next';
import { isEnglishLocale } from '../../utils';

type CoreInvitesDriveMultiProductInvitesState = {
	isCoreInvitesDriveMultiProductInvitesEnabled: boolean;
	getEligibleUsersAndProducts: (
		invitedUsers: (InvitedUser & InvitedUserResult)[],
		products: string[],
	) => Promise<{ products: string[]; users: OptionType[] }>;
	isEnrolled: boolean;
	cohort: string;
};

const EligibleProducts = ['jira', 'jira-software', 'confluence', 'jira-product-discovery'];

export const CoreInvitesDriveMultiProductInvitesStateContext = createContext<
	CoreInvitesDriveMultiProductInvitesState | undefined
>(undefined);

export const CoreInvitesDriveMultiProductInvitesContext = ({
	children,
}: {
	children: React.ReactNode;
}) => {
	const { coreInvitesDriveMultiProductInvites } = useExperimentalFeatures();
	const analytics = useAnalytics(Analytics);
	const { inviteCapabilities, isFetchProductsLoading } = useProducts();
	const { getName, getUser } = useInviteeListStateContext();
	const { cloudId, productId } = useInvitePeopleContext();
	const [
		isCoreInvitesDriveMultiProductInvitesEnabled,
		setIsCoreInvitesDriveMultiProductInvitesEnabled,
	] = useState(false);
	const { locale } = useIntl();

	const cohort = getExperimentCohort({ fireExposure: false });

	const isEnrolled = useMemo(() => {
		if (!isEnglishLocale(locale)) {
			return false;
		}

		if (coreInvitesDriveMultiProductInvites && !isFetchProductsLoading) {
			if (inviteCapabilities?.directInvite.length === 0) {
				analytics.eligibilityChecked('NO_INVITE_PERMISSIONS');
				return false;
			}

			if (!EligibleProducts.includes(productId)) {
				analytics.eligibilityChecked('PRODUCT_NOT_ELIGIBLE');
				return false;
			}

			return coreInvitesDriveMultiProductInvites && (cohort === VARIATION || cohort === CONTROL);
		}
		return false;
	}, [
		analytics,
		inviteCapabilities?.directInvite.length,
		coreInvitesDriveMultiProductInvites,
		isFetchProductsLoading,
		productId,
		cohort,
		locale,
	]);

	const getEligibleUsersAndProducts = useCallback(
		async (invitedUsers: (InvitedUser & InvitedUserResult)[], invitedProducts: string[]) => {
			if (cloudId) {
				const userIds: string[] = invitedUsers.flatMap((user) => (user.id ? user.id : []));

				const filteredEligibleProducts = inviteCapabilities.directInvite
					.map((product) => product.id)
					.filter((x) => !invitedProducts.includes(x) && EligibleProducts.includes(x));

				if (filteredEligibleProducts.length === 0) {
					analytics.eligibilityChecked('NO_ELIGIBLE_PRODUCTS');
					return { products: [], users: [] };
				}
				const productsAndUsers = await defaultPermsApiClient.getUsersWithoutAccess(
					cloudId,
					filteredEligibleProducts,
					userIds,
				);

				const { users, products } = filterUsersAndProductsForDisplay(productsAndUsers);

				const usersWithEmail: OptionType[] = invitedUsers
					.filter((user) => users.includes(user?.id || ''))
					.map((user) => {
						const nameOrEmail = isUserWithEmail(user) ? user.email : getName(user.id);
						const userPickerOption = getUser(user.id!);
						return (
							userPickerOption || {
								id: user.id!,
								email: nameOrEmail,
								name: nameOrEmail,
							}
						);
					});

				const formattedUsersAndProducts = { products, users: usersWithEmail };

				if (users.length === 0) {
					analytics.eligibilityChecked('NO_ELIGIBLE_USERS');
					return formattedUsersAndProducts;
				}
				if (products.length === 0) {
					analytics.eligibilityChecked('NO_ELIGIBLE_PRODUCTS');
					return formattedUsersAndProducts;
				}

				analytics.eligibilityChecked('ELIGIBLE');
				fireStatsigFeatureExposedEvent();
				analytics.featureExposed();

				if (cohort === VARIATION) {
					setIsCoreInvitesDriveMultiProductInvitesEnabled(true);
				}
				return formattedUsersAndProducts;
			}
			return { products: [], users: [] };
		},
		[analytics, cloudId, cohort, getName, getUser, inviteCapabilities.directInvite],
	);

	return (
		<CoreInvitesDriveMultiProductInvitesStateContext.Provider
			value={{
				isCoreInvitesDriveMultiProductInvitesEnabled,
				cohort,
				getEligibleUsersAndProducts,
				isEnrolled,
			}}
		>
			{children}
		</CoreInvitesDriveMultiProductInvitesStateContext.Provider>
	);
};

export const useCoreInvitesDriveMultiProductInvitesContext = () => {
	const state = useContext(CoreInvitesDriveMultiProductInvitesStateContext);

	if (state === undefined) {
		throw new Error(
			'useCoreInvitesDriveMultiProductInvitesContext must be used within a CoreInvitesDriveMultiProductInvitesProvider',
		);
	}

	return state;
};
